import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PageHeader from './PageHeader';

/**
 * PLATFORM-ADMIN guard — « the admin pages must not open by URL » (L3b, 2026-10-12).
 *
 * Why a guard of its own instead of `RequirePageAccess`: `pageVisibility('users' |
 * 'apiKeys')` is `false` for EVERYONE, INCLUDING the superadmin — the sidebar's
 * `admin` section is rendered on `isSuperAdmin || isShowcase`, never on `vis`
 * (see `App.tsx`). Wrapping `/users` with `RequirePageAccess` would therefore LOCK
 * THE ADMIN OUT of his own page; the admin pages need a verdict of their own.
 *
 * SINGLE RULE, ONE EXPRESSION: the same one the sidebar uses to render the `admin`
 * section — `user.global_role === 'superadmin'` (the PLATFORM role, never the
 * band write-role) OR the shared DEMO account (`is_showcase`, which visits the
 * whole product by design). No band capability is consulted: this is not a
 * per-band page.
 *
 * ⚠️ UI ONLY. The real protection has always been server-side (`requireSuperadmin`
 * on the admin API mounts); before this guard, ANY signed-in account could OPEN
 * `/users`, `/api-keys`, `/manager-onboarding` by URL and only the API refused,
 * leaving a broken screen. This component removes that inconsistency, nothing else.
 *
 * `loading`: while the session is being resolved, stay NEUTRAL (an empty page)
 * instead of flashing a refusal — `RequireAuth` sits just above these routes and
 * already handles the loading + redirect-to-login cases.
 */
export default function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, loading } = useAuth();

  // Session not resolved yet — do not judge, do not flash a refusal.
  if (loading) return <div className="page" />;

  // Same verdict as the sidebar `admin` section (superadmin OR the demo account).
  if (user?.global_role === 'superadmin' || !!user?.is_showcase) return <>{children}</>;

  // Refused — same refusal panel as `RequirePageAccess` (shared `pageGuard.*` keys).
  return (
    <div className="page">
      <PageHeader title={t('pageGuard.noAccessTitle')} />
      <p className="empty-hint">{t('pageGuard.noAccess')}</p>
      <p>
        <Link className="nav-link" to="/home">{t('pageGuard.back')}</Link>
      </p>
    </div>
  );
}
